import { FC, useEffect } from "react";

import { getUserFullName, getUserInitials } from "../../utils/user-utils";

import { AccountSettingsModal } from "components/header/AccountSettingsModal";
import { MobileLoginModal } from "components/header/MobileLoginModal";
import { onLogout } from "components/materials/sidebar/helpers";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { ChevronDownIcon, CopySendArrowIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectCurrentUserId,
  selectCurrentUserIsMPAdmin,
  selectIsStudentPreview,
  selectPresentingSessions,
  selectUserById,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useAv } from "utils/av";
import { usePresentation } from "utils/presentation";
import { Link } from "utils/routing";
import { MONITORING_URL, sectionMonitoringDashboardUrl, useViewLevel } from "utils/urls";
import { WaffleFlags, isMobileLoginEnabled, isWaffleFlagEnabled } from "utils/waffle-flags";

export const WaffleFlagModal = () => {
  return (
    <div>
      <div className="text-sm font-bold">Flag Status:</div>
      <div />
      {Object.values(WaffleFlags).map((flag) => (
        <div className="flex items-center gap-2 px-2 py-1 italic" key={flag}>
          <div className="text-sm">{flag}</div>
          {isWaffleFlagEnabled(flag) ? (
            <div className="text-sm text-green">ON</div>
          ) : (
            <div className="text-sm text-red">OFF</div>
          )}
        </div>
      ))}
      <div className="mt-2">
        Flags can be modified at{" "}
        <Link className="!text-blue" target="_blank" to="/api/admin/orgs/orgwaffleflag/">
          /api/admin/orgs/orgwaffleflag/
        </Link>
      </div>
    </div>
  );
};

const showAccountSettingsModal = () => {
  return showConfirmationModal({
    title: t("profile_panel.info_title"),
    children: (onClose) => <AccountSettingsModal onClose={onClose} />,
    hideCancelConfirmButtons: true,
  });
};

const showMobileLoginModal = () => {
  return showConfirmationModal({
    title: t("profile_panel.log_in_with_mobile_device"),
    children: <MobileLoginModal />,
    hideCancelConfirmButtons: true,
  });
};

export const ProfileSettingsMenu: FC = () => {
  const dispatch = useAppDispatch();
  const { canAuthorSection } = useCapabilities();
  const presentingSessions = useAppSelector(selectPresentingSessions);
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentUser = useAppSelector((s) => selectUserById(s, currentUserId));
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const viewLevel = useViewLevel();
  const { isPresenting } = usePresentation();
  const { isInVirtualClass } = useAv();
  const shouldShowSectionSettings = canAuthorSection && !viewLevel.home;
  const shouldShowPreviewAsStudent =
    shouldShowSectionSettings && !isPresenting && !isInVirtualClass;
  const isMPadmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const shouldShowMonitoringDashboardLink = isMPadmin;
  const shouldShowWaffleFlagModal = isMPadmin;
  const shouldShowMobileLogin = isMobileLoginEnabled() && isMPadmin;
  const waffleFlagModalTitle = "Configure Feature Flags";

  useEffect(() => {
    if (currentUserId) {
      storeApi.users.retrieve(currentUserId);
    }
  }, [currentUserId]);

  const MenuButton = (
    <Button
      aria-label={t("profile_panel.info_title")}
      eventName="View Account Menu"
      kind="gray"
      size="s"
      title={t("profile_panel.info_title")}
    >
      <IconText
        className="gap-0.5"
        iconEnd={<ChevronDownIcon />}
        text={getUserInitials(currentUser)}
      />
    </Button>
  );

  return (
    <div className="flex items-center">
      <Menu menuButton={MenuButton} onClick={(e) => e.stopPropagation()}>
        <MenuItem
          aria-label={t("profile_panel.info_title")}
          className="!h-full"
          eventName="Account Settings - Open"
          title={t("profile_panel.info_title")}
          onClick={showAccountSettingsModal}
        >
          <div className="flex h-full flex-col gap-1">
            <div className="flex flex-col text-sm text-black-tint-55">
              <div>{getUserFullName(currentUser)}</div>
              <div>{currentUser?.email || ""}</div>
            </div>
            <div>{t("profile_panel.info_title")}</div>
          </div>
        </MenuItem>
        {shouldShowSectionSettings && (
          <>
            <MenuDivider />
            <MenuItem
              eventName="Section Settings - Open"
              title={t("settings.section_settings")}
              onClick={() => dispatch(viewStateActions.setSectionSettingsTabIndex(0))}
            >
              {t("settings.section_settings")}
            </MenuItem>
          </>
        )}
        {shouldShowPreviewAsStudent && (
          <MenuItem
            disabled={isStudentPreview}
            eventName="Preview as Student - Start"
            title={t("profile_dropdown.student_preview")}
            onClick={() => dispatch(viewStateActions.setIsStudentPreview(true))}
          >
            {t("profile_dropdown.student_preview")}
          </MenuItem>
        )}
        {shouldShowMonitoringDashboardLink && (
          <MenuItem title={t("main_nav.monitoring_dashboard")}>
            <Link
              className="block w-full hover:text-black-tint-20"
              target="_blank"
              to={
                currentSectionId ? sectionMonitoringDashboardUrl(currentSectionId) : MONITORING_URL
              }
            >
              {t("main_nav.monitoring_dashboard")}
            </Link>
          </MenuItem>
        )}
        {shouldShowWaffleFlagModal && (
          <MenuItem
            title={waffleFlagModalTitle}
            onClick={() => {
              showConfirmationModal({
                title: waffleFlagModalTitle,
                children: <WaffleFlagModal />,
                hideCancelConfirmButtons: true,
              });
            }}
          >
            {waffleFlagModalTitle}
          </MenuItem>
        )}
        {shouldShowMobileLogin && (
          <>
            <MenuDivider />
            <MenuItem
              title={t("profile_panel.log_in_with_mobile_device")}
              onClick={showMobileLoginModal}
            >
              {t("profile_panel.log_in_with_mobile_device")}
            </MenuItem>
          </>
        )}
        <MenuDivider />
        <MenuItem
          eventName="Account settings - Logout"
          title={t("profile_panel.logout")}
          onClick={onLogout(presentingSessions, dispatch)}
        >
          <CopySendArrowIcon />
          {t("profile_panel.logout")}
        </MenuItem>
      </Menu>
    </div>
  );
};
